import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Clock, X, Calendar } from "lucide-react";
import {
  useGetRepairRequestQuery,
  useAssignRepairMutation,
  usePostRepairScheduleMutation,
  useGetSchedulesByRepairIDQuery,
  useUpdateRepairScheduleMutation,
} from "../../slices/maintenanceApiSlice";
import {
  useGetUserByEmailQuery,
  useGetUsersQuery,
  useGetDepartmentQuery,
} from "../../slices/userApiSlice";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";

import Select from "react-select";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

const selectUserInfo = (state) => state.auth.userInfo || {};
const getUserEmail = createSelector(selectUserInfo, (userInfo) => {
  const email = userInfo?.username || userInfo?.email || "";
  return email === "null" ? "" : email;
});

const Repair = () => {
  const email = useSelector(getUserEmail);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const rowsPerPage = 10;

  const [modalData, setModalData] = useState(null);
  const [rescheduleModalData, setRescheduleModalData] = useState(null);

  const [selectedPriority, setSelectedPriority] = useState("");
  const [selectedWorkStatus, setSelectedWorkStatus] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const status = searchParams.get("status");
    if (status) {
      setSelectedWorkStatus(status);
    } else {
      setSelectedWorkStatus("");
    }
  }, [searchParams]);

  const [assignedWorker, setAssignedWorker] = useState(null);
  const [assignTime, setAssignTime] = useState(null);
  const [assignDate, setAssignDate] = useState(null);

  const [assignTimeU, setAssignTimeU] = useState(null);
  const [assignDateU, setAssignDateU] = useState(null);

  const [statusPending, setStatusPending] = useState(null);
  const [supervisorWorker, setSupervisorOption] = useState(null);

  const {
    data: repairRequest,
    isLoading: loadingRequest,
    refetch: refetchRepairRequest,
  } = useGetRepairRequestQuery({
    pollingInterval: 3000,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const { data: scheduleData } = useGetSchedulesByRepairIDQuery(
    rescheduleModalData?.repairID,
    {
      skip: !rescheduleModalData,
    },
  );

  // Use Sonner for loading state
  useEffect(() => {
    if (loadingRequest) {
      toast.loading("Loading data, please wait...");
    } else {
      toast.dismiss();
    }
    return () => toast.dismiss();
  }, [loadingRequest]);

  const { data: userByEmial } = useGetUserByEmailQuery(email, {
    skip: !email,
  });

  const { data: users, isLoading: usersLoading } = useGetUsersQuery();
  const { data: departments, isLoading: departmentsLoading } =
    useGetDepartmentQuery();

  const [assignRepair, { isLoading: assigning }] = useAssignRepairMutation();
  const [updateSchedule, { isLoading: updating }] =
    useUpdateRepairScheduleMutation();
  const scheduling = assigning || loadingRequest;

  const supervisorsFromSameAcademy =
    users?.filter(
      (user) =>
        user.academyId?.trim().toLowerCase() ===
        userByEmial?.user.academyId?.trim().toLowerCase() &&
        typeof user.role?.name === "string" &&
        user.role.name.toLowerCase() === "supervisor",
    ) || [];

  const uniqueSupervisorDepartmentIds = [
    ...new Set(supervisorsFromSameAcademy.map((s) => s.departmentId)),
  ];

  const [selectedDepartment, setSelectedDepartment] = useState(null);

  const departmentOptions =
    departments
      ?.filter((dep) =>
        uniqueSupervisorDepartmentIds.includes(dep.departmentId),
      )
      .map((dep) => ({
        value: dep.departmentId,
        label: dep.name,
      })) || [];

  const workerOptions = supervisorsFromSameAcademy
    .filter((user) => user.departmentId === selectedDepartment)
    .map((user) => ({
      value: user.userId,
      label: user.email,
    }));

  const userID = scheduleData?.[0]?.userID;

  const matchedSupervisor = supervisorsFromSameAcademy?.find(
    (supervisor) => supervisor.userId === userID,
  );

  const supervisorEmail = matchedSupervisor?.email;

  const supervisorDepartmentId = matchedSupervisor?.departmentId;
  const supervisorDepartment = Array.isArray(departments)
    ? departments.find((dept) => dept.departmentId === supervisorDepartmentId)
    : null;
  const departmentName = supervisorDepartment?.name || "Unknown";

  // Manage state
  const [selectedDepartmentU, setSelectedDepartmentU] = useState(
    supervisorDepartmentId,
  );
  const [selectedSupervisorId, setSelectedSupervisorId] = useState(null);

  // Update selected department when matchedSupervisor is found
  useEffect(() => {
    if (supervisorDepartmentId) {
      setSelectedDepartmentU(supervisorDepartmentId);
    }
  }, [supervisorDepartmentId, departmentName]);

  // Supervisor select options
  const supervisorsInDepartment =
    supervisorsFromSameAcademy
      ?.filter(
        (user) => String(user.departmentId) === String(selectedDepartmentU),
      )
      .map((user) => ({
        label: user.email,
        value: user.userId,
      })) || [];

  const supervisorOption = supervisorEmail
    ? { label: supervisorEmail, value: matchedSupervisor?.userId }
    : null;

  const finalOptions =
    supervisorsInDepartment.length > 0
      ? supervisorsInDepartment
      : supervisorOption
        ? [supervisorOption]
        : [];

  // Department dropdown options
  const departmentOptionsU =
    departments
      ?.filter((dep) =>
        uniqueSupervisorDepartmentIds.includes(dep.departmentId),
      )
      .map((dep) => ({
        value: dep.departmentId,
        label: dep.name,
      })) || [];
  // For default department selection
  const initialDepartmentOption = departmentOptionsU.find(
    (opt) => opt.label === departmentName,
  );

  // Set reporting time/date
  useEffect(() => {
    if (Array.isArray(scheduleData) && scheduleData.length > 0) {
      const schedule = scheduleData[0];
      setAssignDateU(schedule.reportingDate);
      setAssignTimeU(schedule.startTime);
    }
  }, [scheduleData]);

  const handleRescheduleView = async (item) => {
    setRescheduleModalData(item);
    setAssignDateU(item?.reportingDate || "");
    setAssignTimeU(item?.startTime?.slice(0, 5) || "");
    setStatusPending(item?.status);
  };

  const [postRepairSchedule] = usePostRepairScheduleMutation();

  const handleAssignAndSchedule = async () => {
    if (!assignedWorker || !assignTime || !assignDate || !modalData?.repairID) {
      toast.warning("Warning", {
        description: "Please fill all required fields.",
      });
      return;
    }
    const formattedDate = new Date(assignDate).toISOString().split("T")[0];
    const scheduleData = {
      repairID: modalData.repairID,
      reportingDate: formattedDate,
      startTime: `${assignTime}:00`,
      userID: assignedWorker?.value,
      technicianEmail: assignedWorker?.label,
    };

    try {
      await assignRepair({
        repairId: modalData.repairID,
        email: assignedWorker?.label,
      });

      await postRepairSchedule(scheduleData);

      toast.success("Repair Request Scheduled", {
        description:
          "The request has been successfully assigned and scheduled.",
      });

      refetchRepairRequest();
      handleCloseModal();
    } catch (error) {
      toast.error("Error", {
        description:
          error?.data?.message ||
          JSON.stringify(error) ||
          "Something went wrong",
      });
    }
  };

  const handleUpdateSchedule = async () => {
    if (!scheduleData?.[0]?.scheduleID) {
      toast.error("Schedule Not Found", {
        description:
          "We couldn't locate the schedule data. Please check and try again.",
      });
      return;
    }
    const scheduleId = scheduleData[0].scheduleID;

    let formattedTime = assignTimeU;
    if (assignTimeU.length === 5) {
      formattedTime = `${assignTimeU}:00`;
    }

    const updatedData = {
      startTime: formattedTime,
      reportingDate: assignDateU,
      userID: selectedSupervisorId || supervisorWorker?.value,
      repairID: scheduleData[0].repairID,
      technicianEmail: supervisorWorker?.label,
    };

    try {
      await assignRepair({
        repairId: scheduleData[0].repairID,
        email: supervisorWorker?.label,
      });

      await updateSchedule({ scheduleId, updatedData }).unwrap();

      toast.success("Schedule Updated", {
        description: "Repair has been reassigned successfully.",
      });

      refetchRepairRequest();
      handleCloseModal2();
    } catch (error) {
      toast.error("Error", {
        description: error?.data?.message || "Update failed",
      });
      console.error("Update schedule error:", error);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  const [data, setData] = useState([]);

  useEffect(() => {
    const userAcademy = userByEmial?.user?.academyId?.trim().toLowerCase();

    if (!userAcademy || !repairRequest) return;

    const filtered = repairRequest.filter((req) => {
      const requestAcademy = req.academyId?.trim().toLowerCase();
      return requestAcademy === userAcademy && req.accept === true;
    });

    const sortedFiltered = filtered.sort((a, b) => {
      if (!a.repairID || !b.repairID) return 0;
      return b.repairID.localeCompare(a.repairID);
    });
    setData(sortedFiltered);
  }, [repairRequest, userByEmial]);

  const getWorkOrderStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "bg-yellow-100 text-yellow-800 hover:bg-yellow-100";
      case "inprogress":
      case "in progress":
        return "bg-blue-100 text-blue-800 hover:bg-blue-100";
      case "completed":
        return "bg-green-100 text-green-800 hover:bg-green-100";
      default:
        return "bg-gray-100 text-gray-800 hover:bg-gray-100";
    }
  };

  const uniquePriorities = [
    { value: "", label: "All Priorities" },
    ...Array.from(
      new Set(data.map((item) => item.priority?.toLowerCase()).filter(Boolean)),
    ).map((priority) => ({
      value: priority,
      label: priority.charAt(0).toUpperCase() + priority.slice(1),
    })),
  ];


  const filteredData = data.filter((item) => {
    const matchesSearch = Object.values(item).some((value) =>
      (value || "").toString().toLowerCase().includes(searchTerm.toLowerCase()),
    );

    const matchesPriority =
      selectedPriority === "" ||
      item.priority?.toLowerCase() === selectedPriority.toLowerCase();

    const matchesWorkStatus =
      selectedWorkStatus === "" ||
      item.status?.toLowerCase() === selectedWorkStatus.toLowerCase();

    return matchesSearch && matchesPriority && matchesWorkStatus;
  });

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const displayedData = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  console.log("dd", displayedData);

  const handleScheduleView = (item) => {
    setModalData(item);
    setAssignedWorker(null);
    setAssignDate("");
    setAssignTime("");
    setSelectedDepartment(null);
  };

  const handleCloseModal = () => {
    setModalData(null);
    setAssignedWorker(null);
    setAssignDate(null);
    setAssignTime(null);
    setSelectedDepartment(null);
    refetchRepairRequest();
  };

  const handleCloseModal2 = () => {
    setRescheduleModalData(null);
    setAssignedWorker(null);
    setAssignDate(null);
    setAssignTime(null);
    setSelectedDepartment(null);
    setSelectedSupervisorId(null);
    refetchRepairRequest();
  };

  const getLocalCurrentTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  useEffect(() => {
    if (modalData) {
      setAssignTime(getLocalCurrentTime());
    }
  }, [modalData]);

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="px-6 py-4">
          <h1 className="text-2xl font-semibold tracking-tight">
            Repair Maintenance
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage and track repair maintenance requests and work orders
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="border-b bg-card px-6 py-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              type="text"
              placeholder="Search repairs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <select
            value={selectedPriority}
            onChange={(e) => setSelectedPriority(e.target.value)}
            className="flex h-10 w-full md:w-[180px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">All Priorities</option>
            {Array.from(
              new Set(
                data
                  .map((item) => item.priority?.toLowerCase())
                  .filter(Boolean),
              ),
            ).map((priority) => (
              <option key={priority} value={priority}>
                {priority.charAt(0).toUpperCase() + priority.slice(1)}
              </option>
            ))}
          </select>

        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto p-6">
        <div className="rounded-md border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="font-semibold">Sl. No.</TableHead>
                <TableHead className="font-semibold">Image</TableHead>
                <TableHead className="font-semibold">Name</TableHead>
                {/* <TableHead className="font-semibold">Email</TableHead> */}
                {/* <TableHead className="font-semibold">Phone</TableHead> */}
                <TableHead className="font-semibold">Asset Name</TableHead>
                <TableHead className="font-semibold">Description</TableHead>
                <TableHead className="font-semibold">Area</TableHead>
                <TableHead className="font-semibold">Priority</TableHead>
                <TableHead className="font-semibold">Work Status</TableHead>
                <TableHead className="font-semibold">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayedData.length > 0 ? (
                displayedData.map((item, index) => (
                  <TableRow 
                    key={item.repairID || index}
                    className="hover:bg-[#305845]/5 transition-colors cursor-pointer"
                    onClick={() =>
                      item.scheduled === false
                        ? handleScheduleView(item)
                        : handleRescheduleView(item)
                    }
                  >
                    <TableCell>
                      {(currentPage - 1) * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell>
                      {item.images && item.images[0] ? (
                        <img
                          src={item.images[0]}
                          alt="Repair"
                          className="w-24 h-24 object-cover rounded"
                        />
                      ) : (
                        <div className="w-24 h-24 bg-muted rounded flex items-center justify-center text-xs text-muted-foreground">
                          No image
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{item.name || "-"}</TableCell>
                    <TableCell>{item.assetName || "-"}</TableCell>
                    <TableCell>{item.description || "-"}</TableCell>
                    <TableCell>{item.area || "-"}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{item.priority || "-"}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={getWorkOrderStatusBadge(item.status)}>
                        {item.status || "-"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {item.status === "Completed" ||
                        item.status === "In Progress" ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            item.scheduled === false
                              ? handleScheduleView(item)
                              : handleRescheduleView(item)
                          }
                        >
                          View
                        </Button>
                      ) : item.scheduled === false ? (
                        <Button
                          size="sm"
                          onClick={() => handleScheduleView(item)}
                        >
                          <Clock className="h-4 w-4 mr-1" />
                          Schedule
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleRescheduleView(item)}
                        >
                          <Calendar className="h-4 w-4 mr-1" />
                          Reschedule
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No repair requests found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      <div className="border-t bg-card px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing{" "}
            <span className="font-medium text-foreground">
              {displayedData.length > 0
                ? (currentPage - 1) * rowsPerPage + 1
                : 0}
            </span>{" "}
            to{" "}
            <span className="font-medium text-foreground">
              {Math.min(currentPage * rowsPerPage, filteredData.length)}
            </span>{" "}
            of{" "}
            <span className="font-medium text-foreground">
              {filteredData.length}
            </span>{" "}
            results
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <div className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages || 1}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages || totalPages === 0}
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      {/* Schedule Modal */}
      <Dialog
        open={!!modalData}
        onOpenChange={(open) => !open && handleCloseModal()}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Schedule Form</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Department</Label>
              <Select
                classNamePrefix="custom-select-department"
                options={departmentOptions}
                value={
                  departmentOptions.find(
                    (opt) => opt.value === selectedDepartment,
                  ) || null
                }
                onChange={(option) =>
                  setSelectedDepartment(option?.value || "")
                }
                isLoading={departmentsLoading}
                isClearable
                placeholder="Select department..."
              />
            </div>

            <div className="space-y-2">
              <Label>Assign Supervisor</Label>
              <Select
                classNamePrefix="custom-select-department"
                options={workerOptions}
                value={assignedWorker}
                onChange={(selectedOption) => {
                  setAssignedWorker(selectedOption || null);
                }}
                isClearable
                placeholder="Select supervisor..."
              />
            </div>

            <div className="space-y-2">
              <Label>Assign Date</Label>
              <Input
                type="date"
                value={assignDate || ""}
                min={today}
                onChange={(e) => setAssignDate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Assign Time</Label>
              <Input type="time" value={assignTime || ""} readOnly />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button onClick={handleAssignAndSchedule} disabled={scheduling}>
              {scheduling ? "Scheduling..." : "Schedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule Modal */}
      <Dialog
        open={!!rescheduleModalData}
        onOpenChange={(open) => !open && handleCloseModal2()}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {statusPending === "Pending"
                ? "Reschedule Form"
                : "Schedule Details"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Department</Label>
              <Select
                classNamePrefix="custom-select-department"
                options={departmentOptionsU}
                value={
                  departmentOptionsU.find(
                    (opt) => opt.value === selectedDepartmentU,
                  ) ||
                  initialDepartmentOption ||
                  null
                }
                onChange={(selected) => setSelectedDepartmentU(selected.value)}
                placeholder="Select department..."
              />
            </div>

            <div className="space-y-2">
              <Label>Assign Supervisor</Label>
              <Select
                classNamePrefix="custom-select-department"
                value={
                  finalOptions.find(
                    (opt) => opt.value === selectedSupervisorId,
                  ) ||
                  supervisorOption ||
                  null
                }
                options={finalOptions}
                isClearable
                onChange={(selectedOption) => {
                  setSelectedSupervisorId(selectedOption?.value || null);
                  setSupervisorOption(selectedOption || null);
                }}
                placeholder="Select supervisor..."
              />
            </div>

            <div className="space-y-2">
              <Label>Assign Date</Label>
              <Input
                type="date"
                value={
                  assignDateU !== ""
                    ? assignDateU
                    : rescheduleModalData?.reportingDate || ""
                }
                min={today}
                onChange={(e) => setAssignDateU(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Assign Time</Label>
              <Input
                type="time"
                value={
                  assignTimeU !== ""
                    ? assignTimeU
                    : rescheduleModalData?.startTime?.slice(0, 5) || ""
                }
                onChange={(e) => setAssignTimeU(getLocalCurrentTime())}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseModal2}>
              Close
            </Button>
            {statusPending === "Pending" && (
              <Button disabled={updating} onClick={handleUpdateSchedule}>
                {updating ? "Saving..." : "Save"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Repair;
